import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import CustomTable from '../../../../../components/CustomTable';
import CustomModal from '../../../../../components/CustomModal';
import CustomInput from '../../../../../components/CustomInput';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { Space } from 'antd';
import { toast } from 'react-toastify';
import {
    allPushDrivers,
    pushDriversLoadingStatus,
    getPushDrivers,
    createPushDriver,
    updatePushDriver,
    deletePushDriver,
} from '../../../../../redux/slices/settingsSlice';

export default function PushDriversSubPage() {
    const dispatch = useDispatch();

    const pushDriversStatus = useSelector(pushDriversLoadingStatus);
    const pushDrivers = useSelector(allPushDrivers);

    const [isCreateDriverModalVisible, setIsCreateDriverModalVisible] =
        useState(false);
    const [isEditDriverModalVisible, setIsEditDriverModalVisible] =
        useState(false);
    const [isDeleteDriverModalVisible, setIsDeleteDriverModalVisible] =
        useState(false);
    const [data, setData] = useState({
        name: '',
        key: '',
    });
    const [selectedDriver, setSelectedDriver] = useState({
        id: null,
        name: '',
    });
    const [errors, setErrors] = useState({});

    useEffect(() => {
        dispatch(getPushDrivers());
    }, [dispatch]);

    const resetState = () => {
        setData({
            name: '',
            key: '',
        });
    };

    const toggleCreateDriverModal = () => {
        resetState();
        setErrors({});
        setIsCreateDriverModalVisible(!isCreateDriverModalVisible);
    };

    const toggleEditDriverModal = (record) => {
        if (record === undefined) {
            setIsEditDriverModalVisible(!isEditDriverModalVisible);
        } else {
            setData({
                name: record.name,
                key: record.key,
            });
            setSelectedDriver({ id: record.id, name: record.name });
            setErrors({});
            setIsEditDriverModalVisible(!isEditDriverModalVisible);
        }
    };

    const toggleDeleteDriverModal = (record) => {
        if (record === undefined) {
            setIsDeleteDriverModalVisible(!isDeleteDriverModalVisible);
        } else {
            setSelectedDriver({ id: record.id, name: record.name });
            setIsDeleteDriverModalVisible(!isDeleteDriverModalVisible);
        }

        setErrors({});
    };

    const onInputChange = (e) => {
        const value = e.target.value;
        setData({
            ...data,
            [e.target.id]: value,
        });
    };

    const onCreateDriver = async () => {
        try {
            await dispatch(createPushDriver(data)).unwrap();
            toast.success('New Push driver successfully added');
            toggleCreateDriverModal();
            dispatch(getPushDrivers());
        } catch (error) {
            setErrors(error.errors);
        }
    };

    const onEditDriver = async () => {
        try {
            await dispatch(
                updatePushDriver({ id: selectedDriver.id, body: data }),
            ).unwrap();
            toast.success('Changes saved successfully');
            toggleEditDriverModal();
            dispatch(getPushDrivers());
        } catch (error) {
            setErrors(error.errors);
        }
    };

    const onDeleteDriver = async () => {
        try {
            await dispatch(deletePushDriver(selectedDriver.id)).unwrap();
            toast.success('Push driver successfully deleted');
            toggleDeleteDriverModal();
            dispatch(getPushDrivers());
        } catch (error) {
            setErrors(error.errors);
        }
    };

    const getFilteredDrivers = (data, filter) => {
        const filteredData = data.filter((el) =>
            el?.name.toLowerCase().includes(filter.toLowerCase()),
        );

        return filteredData;
    };

    const actionsOption = {
        title: 'Actions',
        render: (_, record) => (
            <Space size="middle">
                <EditOutlined onClick={(e) => toggleEditDriverModal(record)} />
                <DeleteOutlined
                    onClick={(e) => toggleDeleteDriverModal(record)}
                />
            </Space>
        ),
    };

    return (
        <div>
            <Helmet>
                <title>
                    Console Defentry - Settings - Notifications - Push drivers
                </title>
            </Helmet>
            <div className="table-wrapper">
                <CustomTable
                    tableHeader={['Name', 'Key', 'Actions']}
                    functionalCell={{ Actions: actionsOption }}
                    onAddButtonClick={toggleCreateDriverModal}
                    tablePannelTitle="List of drivers"
                    searchInputPlaceholder="Search by name"
                    topPannelButtonText="Create driver"
                    data={pushDrivers}
                    getFilteredData={getFilteredDrivers}
                    isLoading={pushDriversStatus === 'loading'}
                    withFilter
                />
            </div>
            <CustomModal
                title="Create driver"
                subText="Please fill the fields below"
                subTextAlign="left"
                isModalVisible={isCreateDriverModalVisible}
                onAcceptButtonClick={onCreateDriver}
                onCancelButtonClick={toggleCreateDriverModal}
                cancelButtonText="Cancel"
                acceptButtonText="Create"
            >
                <div className="form__item-wrapper">
                    <CustomInput
                        id="name"
                        placeholder="Enter name"
                        onInputChange={onInputChange}
                        inputValue={data.name}
                        labelText="Name"
                        width="100%"
                        errorText={errors?.name}
                    />
                </div>
                <div className="form__item-wrapper">
                    <CustomInput
                        id="key"
                        placeholder="Enter key"
                        onInputChange={onInputChange}
                        inputValue={data.key}
                        labelText="Key"
                        width="100%"
                        errorText={errors?.key}
                    />
                </div>
            </CustomModal>
            <CustomModal
                title="Edit driver"
                subText="Please fill the fields below"
                subTextAlign="left"
                isModalVisible={isEditDriverModalVisible}
                onAcceptButtonClick={onEditDriver}
                onCancelButtonClick={toggleEditDriverModal}
                cancelButtonText="Cancel"
                acceptButtonText="Update"
            >
                <div className="form__item-wrapper">
                    <CustomInput
                        id="name"
                        placeholder="Enter name"
                        onInputChange={onInputChange}
                        inputValue={data.name}
                        labelText="Name"
                        width="100%"
                        errorText={errors?.name}
                    />
                </div>
                <div className="form__item-wrapper">
                    <CustomInput
                        id="key"
                        placeholder="Enter key"
                        onInputChange={onInputChange}
                        inputValue={data.key}
                        labelText="Key"
                        width="100%"
                        errorText={errors?.key}
                    />
                </div>
            </CustomModal>
            <CustomModal
                title={
                    <p>
                        Are you sure you want to delete
                        <span className="highlighted-text">{` ${selectedDriver.name} `}</span>
                        driver?
                    </p>
                }
                subText="You will delete this driver without recovery"
                subTextAlign="center"
                isModalVisible={isDeleteDriverModalVisible}
                onAcceptButtonClick={onDeleteDriver}
                onCancelButtonClick={toggleDeleteDriverModal}
                cancelButtonText="Cancel"
                acceptButtonText="Delete"
            >
                {errors.push_driver_id && (
                    <p className="modal__error-text">
                        {errors.push_driver_id[0]}
                    </p>
                )}
            </CustomModal>
        </div>
    );
}
